import {
  BatchDefinitionsItemAPI,
  JobDefinitionsAPI,
  LookupsResourcesAPI,
  ParametersListAPI,
  ProjectsBatchesListAPI,
  ProjectsJobsListAPI,
  ScheduledBatchesAPI,
} from 'ducks/types/api-types';
import { BatchDefinition, JobDefinitionItem, JobItem, ParametersItem, ScheduledBatch } from 'ducks/types/store-types';
import { TReturnType, WithUUID } from 'ducks/types/type-helpers';
import moment from 'moment';
import { formatDate, formatTimeout, offsetTimezoneUTC } from 'utils/dates';
import { parseDockerImg } from 'utils/helpers';
import { v4 as uuidv4 } from 'uuid';

import 'moment-duration-format';

export function normalizeParameters(parameters: ParametersListAPI[]): ParametersItem[] {
  return parameters
    .map((parameter) => ({
      ...parameter,
      parameter_name_old: parameter.parameter_name,
      parameter_value_disabled: !!parameter.reference_type_id,
      modified: true,
      saved: true,
      delete: false,
      uuid: uuidv4(),
    }))
    .sort((a, b) => (a.parameter_name > b.parameter_name ? 1 : -1))
    .sort((a, b) => (a.is_required === b.is_required ? 0 : a.is_required ? -1 : 1));
}

export function normalizeBatchDefinition(data: BatchDefinitionsItemAPI): BatchDefinition {
  return {
    ...data,
    // timeout_seconds_masked: data.timeout_seconds && moment.utc(data.timeout_seconds * 1000).format('hh:mm'),
    timeout_seconds_masked: '',
    sort_datetime_utc: formatDate(data.created_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    created_datetime_utc_formatted: formatDate(data.created_datetime_utc, { format: 'M/D/YY h:mm:ss A' }),
    sort_datetime: offsetTimezoneUTC(data.created_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    created_datetime_formatted: offsetTimezoneUTC(data.created_datetime_utc, { format: 'M/D/YY h:mm A' }),
    uuid: uuidv4(),
  };
}

export function normalizeArrayUUID<T>(array: T[] = []): WithUUID<T>[] {
  return array.map((a) => ({ ...a, uuid: uuidv4() }));
}

export function normalizeObjUUID<T extends object>(obj: T): WithUUID<T> {
  return { ...obj, uuid: uuidv4() };
}

export function normalizeJobDefinition(data: JobDefinitionsAPI): JobDefinitionItem {
  return {
    ...data,
    modified: false,
    prevent_preemption_name: data.prevent_preemption ? 'Yes' : 'No',
    timeout_seconds_masked: formatTimeout(data.timeout_seconds),
    sort_datetime_utc: formatDate(data.created_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    created_datetime_utc_formatted: formatDate(data.created_datetime_utc, { format: 'M/D/YY h:mm:ss A' }),
    sort_datetime: offsetTimezoneUTC(data.created_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    local_timezone: offsetTimezoneUTC(data.created_datetime_utc, { format: 'M/D/YY h:mm:ss A' }),
    created_datetime_formatted: offsetTimezoneUTC(data.created_datetime_utc, { format: 'M/D/YY h:mm A' }),
    uuid: uuidv4(),
  };
}

export function normalizeJob(data: ProjectsJobsListAPI): JobItem {
  const {
    job_id,
    job_definition_name,
    batch_descriptor,
    duration_seconds,
    timeout_seconds,
    start_datetime_utc,
    finish_datetime_utc,
    job_state_name,
    prevent_preemption,
    docker_image,
  } = data;

  return {
    ...data,
    prevent_preemption_name: prevent_preemption ? 'Yes' : 'No',
    prevent_preemption: false,
    job_name: `${job_id} ${job_definition_name}`,
    job_sort: `${job_definition_name} ${batch_descriptor}`,
    timeout_percentage: duration_seconds && timeout_seconds && duration_seconds / timeout_seconds,
    timeout_seconds_masked: formatTimeout(data.timeout_seconds),
    duration_days_masked: moment.duration(duration_seconds, 'seconds').format('d[d]'),
    duration_hours_masked: moment.duration(duration_seconds, 'seconds').format('d[d] h[h]'),
    duration_minutes_masked: moment.duration(duration_seconds, 'seconds').format('d[d] h[h] m[m]'),
    duration_seconds_masked: moment.duration(duration_seconds, 'seconds').format('d[d] h[h] m[m] s[s]'),
    duration_days_show: duration_seconds > 864000, // 10 day
    duration_hours_show: duration_seconds > 86400, // 1 day
    duration_minutes_show: duration_seconds > 3600, // 1 hour
    docker_image_formatted: parseDockerImg(docker_image),
    job_state_name_formatted: job_state_name.split('-')[1],
    sort_datetime_utc: formatDate(start_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    start_datetime_utc_formatted: formatDate(start_datetime_utc, { format: 'M/D/YY h:mm:ss A' }),
    finish_datetime_utc_formatted: formatDate(finish_datetime_utc || new Date(), { format: 'M/D/YY h:mm:ss A' }),
    sort_datetime: offsetTimezoneUTC(start_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    start_datetime_formatted: offsetTimezoneUTC(start_datetime_utc, { format: 'M/D/YY h:mm A' }),
    finish_datetime_formatted: offsetTimezoneUTC(finish_datetime_utc || new Date(), { format: 'M/D/YY h:mm A' }),
    uuid: uuidv4(),
  };
}

export function normalizeScheduledBatch(data: ScheduledBatchesAPI): ScheduledBatch {
  return {
    ...data,
    sort_datetime_utc: formatDate(data.created_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    // timeout_seconds_masked: data.timeout_seconds ? moment.utc(data.timeout_seconds * 1000).format('hh:mm') : '',
    timeout_seconds_masked: '',
    start_datetime_utc_slim: formatDate(data.start_datetime_utc, { format: 'M/D/YY hh:mm A' }),
    start_datetime_utc_sort: formatDate(data.start_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    start_datetime_utc_formatted: formatDate(data.start_datetime_utc, { format: 'M/D/YYYY hh:mm A' }),
    start_datetime_slim: offsetTimezoneUTC(data.start_datetime_utc, { format: 'M/D/YY hh:mm A' }),
    start_datetime_sort: offsetTimezoneUTC(data.start_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    start_datetime_formatted: offsetTimezoneUTC(data.start_datetime_utc, { format: 'M/D/YYYY hh:mm A' }),
    last_datetime_utc_sort: formatDate(data.last_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    last_datetime_utc_formatted: formatDate(data.last_datetime_utc, { format: 'M/D/YY hh:mm A' }),
    last_datetime_sort: offsetTimezoneUTC(data.last_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    last_datetime_formatted: offsetTimezoneUTC(data.last_datetime_utc, { format: 'M/D/YY hh:mm A' }),
    next_datetime_utc_slim: formatDate(data.next_datetime_utc, { format: 'M/D/YY hh:mm A' }),
    next_datetime_utc_sort: formatDate(data.next_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    next_datetime_utc_formatted: formatDate(data.next_datetime_utc, { format: 'M/D/YYYY hh:mm A' }),
    next_datetime_slim: offsetTimezoneUTC(data.next_datetime_utc, { format: 'M/D/YY hh:mm A' }),
    next_datetime_sort: offsetTimezoneUTC(data.next_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    next_datetime_formatted: offsetTimezoneUTC(data.next_datetime_utc, { format: 'M/D/YYYY hh:mm A' }),
    finish_datetime_utc_formatted: formatDate(data.finish_datetime_utc, { format: 'M/D/YYYY hh:mm A' }),
    finish_datetime_formatted: offsetTimezoneUTC(data.finish_datetime_utc, { format: 'M/D/YYYY hh:mm A' }),
    created_datetime_formatted: offsetTimezoneUTC(data.created_datetime_utc, { format: 'M/D/YY h:mm A' }),
    created_datetime_utc_formatted: formatDate(data.created_datetime_utc, { format: 'M/D/YY h:mm A' }),
    interval_bitmask: normalizeBitmaskInterval(data.interval_bitmask),
    uuid: uuidv4(),
    modified: false,
  };
}

const normalizeBitmaskInterval = (interval: number): string => {
  if (+interval === 0) {
    return '0000000';
  }
  const stringify = interval.toString();
  const missed_elements = 7 - stringify.length;
  const missed_zeros = Array.from(Array(missed_elements), () => 0).join('');
  return `${missed_zeros}${stringify}`;
};

export const clearStore = () => {
  // need to clear stuff
};

export function normalizeLookups<TName extends string, TId extends string>(
  array: LookupsResourcesAPI[],
  name: TName,
  id: TId,
): Array<TReturnType<TName, TId>> {
  return array.map(
    (a) =>
      ({
        [name]: a.value,
        [id]: a.key,
        uuid: uuidv4(),
      } as TReturnType<TName, TId>),
  );
}

export function normalizeBatches(batches: ProjectsBatchesListAPI[]) {
  return batches.map((batch) => normalizeBatch(batch)).sortObject('batch_name', 'desc');
}

const setBatchState = (data: ProjectsBatchesListAPI) => {
  const {
    failed_job_count,
    succeeded_job_count,
    queued_job_count,
    running_job_count,
    starting_job_count,
    stopped_job_count,
  } = data;
  const batch_running_count = queued_job_count + running_job_count + starting_job_count;
  if (failed_job_count > 0) {
    return {
      batch_state_name: 'Failed',
      batch_state_sort: '99 Failed',
      batch_state_id: 99,
    };
  }
  if (stopped_job_count > 0) {
    return {
      batch_state_name: 'Stopped',
      batch_state_sort: '98 Stopped',
      batch_state_id: 98,
    };
  }
  if (batch_running_count > 0) {
    return {
      batch_state_name: 'Running',
      batch_state_sort: '03 Running',
      batch_state_id: 3,
    };
  }
  if (succeeded_job_count > 0) {
    return {
      batch_state_name: 'Succeeded',
      batch_state_sort: '04 Succeeded',
      batch_state_id: 4,
    };
  }
  return {
    batch_state_name: 'Created',
    batch_state_sort: '01 Created',
    batch_state_id: 1,
  };
};

export function normalizeBatch(data: ProjectsBatchesListAPI) {
  const batch_states = setBatchState(data);
  return {
    ...data,
    ...batch_states,
    sort_datetime_utc: formatDate(data.created_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    created_datetime_utc_formatted: formatDate(data.created_datetime_utc, { format: 'M/D/YY h:mm:ss A' }),
    sort_datetime: offsetTimezoneUTC(data.created_datetime_utc, { format: 'YYYYMMDDHHmm' }),
    local_timezone: offsetTimezoneUTC(data.created_datetime_utc, { format: 'M/D/YY h:mm:ss A' }),
    created_datetime_formatted: offsetTimezoneUTC(data.created_datetime_utc, { format: 'M/D/YY h:mm A' }),
    uuid: uuidv4(),
  };
}
